import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { CheckCircle, ArrowRight, ArrowLeft, Sparkles, ChevronDown, ChevronUp, User, BookOpen } from 'lucide-react';
import { PART1_QUESTIONS, SYMPTOMS, PILLAR_DETAILS, CATEGORY_TO_SUBCATEGORY, getCategoryClassroomUrl } from './wellnessAssessmentData';
import { toast } from 'react-toastify';

const API = process.env.REACT_APP_BACKEND_URL;

const ProgressiveOnboarding = ({ user, isOpen, onClose, onComplete }) => {
  // Flow steps: welcome -> part1 -> part2 -> results -> profile (optional)
  const [flowStep, setFlowStep] = useState('welcome');
  const [part1Answers, setPart1Answers] = useState({});
  const [primaryPillar, setPrimaryPillar] = useState(null);
  const [part2Symptoms, setPart2Symptoms] = useState({});
  const [expandedPillar, setExpandedPillar] = useState(null);
  const [results, setResults] = useState(null);
  const [saving, setSaving] = useState(false);
  const [matchedArticles, setMatchedArticles] = useState({});

  // Profile setup state
  const [profileData, setProfileData] = useState({
    fun_facts: '',
    wellness_goals: [],
    topics_of_interest: [],
    experience_level: ''
  });
  const [profileStep, setProfileStep] = useState(1);

  const goalsOptions = [
    'Symptom relief', 'Community support', 'Lifestyle optimization',
    'Education and learning', 'Guidance and coaching', 'Overall wellbeing',
    'Movement and fitness advice'
  ];

  const topicsOptions = [
    'Peptides', 'Supplements', 'Skincare', 'Exercise and fitness',
    'Sleep optimization', 'Alternative therapies', 'Sexual health',
    'Brain optimization', 'Product ideas'
  ];

  const experienceLevels = [
    { value: 'just_starting', label: 'Just starting my journey' },
    { value: 'researching', label: 'Researching options' },
    { value: 'on_treatment', label: 'Currently on treatment' },
    { value: 'experienced', label: 'Experienced & optimizing' }
  ];

  // Quiz logic
  const calculatePrimaryPillar = () => {
    const scores = { BODY: 0, MIND: 0, VITALITY: 0 };
    Object.values(part1Answers).forEach(answer => {
      if (answer === 'A') scores.BODY++;
      else if (answer === 'B') scores.MIND++;
      else if (answer === 'C') scores.VITALITY++;
    });
    return Object.entries(scores).sort((a, b) => b[1] - a[1])[0][0];
  };

  const handlePart1Complete = () => {
    const pillar = calculatePrimaryPillar();
    setPrimaryPillar(pillar);
    setExpandedPillar(pillar);
    setFlowStep('part2');
    window.scrollTo({ top: 0 });
  };

  const isPart1Complete = Object.keys(part1Answers).length === PART1_QUESTIONS.length;

  const hasPart2Symptoms = Object.values(part2Symptoms).some(arr => arr?.length > 0);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const pillarSymptomCounts = { BODY: 0, MIND: 0, VITALITY: 0 };
      const topCategories = [];
      Object.entries(part2Symptoms).forEach(([category, syms]) => {
        if (syms?.length > 0) {
          const mapping = CATEGORY_TO_SUBCATEGORY[category];
          if (mapping) {
            if (mapping.pillar === 'radiant_body') pillarSymptomCounts.BODY += syms.length;
            else if (mapping.pillar === 'balanced_mind') pillarSymptomCounts.MIND += syms.length;
            else if (mapping.pillar === 'enduring_vitality') pillarSymptomCounts.VITALITY += syms.length;
          }
          topCategories.push(category);
        }
      });

      const totalSymptoms = Object.values(pillarSymptomCounts).reduce((s, v) => s + v, 0);

      const calculatedResults = {
        primary_pillar: primaryPillar,
        top_categories: topCategories.slice(0, 5),
        total_symptoms: totalSymptoms,
        pillar_symptom_counts: pillarSymptomCounts
      };

      // Save to backend
      await axios.post(`${API}/api/users/${user.id}/wellness-assessment`, {
        primary_pillar: primaryPillar,
        top_subcategories: topCategories.slice(0, 5),
        part1_answers: part1Answers,
        part2_symptoms: part2Symptoms
      });

      // Track assessment event
      try {
        await axios.post(`${API}/api/tracking/assessment`, {
          primary_pillar: primaryPillar,
          top_subcategories: topCategories.slice(0, 5),
          total_symptoms: totalSymptoms,
          source: 'onboarding'
        });
      } catch (trackErr) {
        console.log('Tracking error (non-critical):', trackErr);
      }

      setResults(calculatedResults);
      setFlowStep('results');
      
      // Fetch matching articles for the result tags
      try {
        const subcats = topCategories.slice(0, 5).join(',');
        const articlesRes = await axios.get(`${API}/api/content/by-subcategories?subcategories=${encodeURIComponent(subcats)}`);
        setMatchedArticles(articlesRes.data || {});
      } catch (err) {
        console.log('Could not fetch matching articles:', err);
      }
    } catch (error) {
      console.error('Error saving assessment:', error);
      toast.error('Error saving your assessment. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleProfileSubmit = async () => {
    setSaving(true);
    try {
      // Extract symptoms from quiz for profile
      const symptomsList = [];
      Object.entries(part2Symptoms).forEach(([category, syms]) => {
        if (syms?.length > 0) symptomsList.push(category);
      });

      await axios.post(`${API}/api/users/${user.id}/onboarding`, {
        ...profileData,
        symptoms: symptomsList
      });
      toast.success('Profile set up! Welcome to Luminelle.');
      onComplete();
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Error saving profile. You can update it later from settings.');
      onComplete();
    } finally {
      setSaving(false);
    }
  };

  const handleExploreContent = async () => {
    // Mark onboarding as complete and navigate to classroom
    try {
      await axios.post(`${API}/api/users/${user.id}/skip-onboarding`);
    } catch (err) {
      console.log('Skip onboarding error:', err);
    }
    onComplete('classroom');
  };

  const handleSkip = async () => {
    try {
      await axios.post(`${API}/api/users/${user.id}/skip-onboarding`);
    } catch (err) {
      console.log('Skip error:', err);
    }
    toast('You can retake the assessment anytime from Tools');
    onComplete();
  };

  const pillarOrder = primaryPillar
    ? [primaryPillar, ...['BODY', 'MIND', 'VITALITY'].filter(p => p !== primaryPillar)]
    : ['BODY', 'MIND', 'VITALITY'];

  const progressSteps = ['welcome', 'part1', 'part2', 'results'];
  const progressLabels = ['Welcome', 'Focus Area', 'Symptoms', 'Your Plan'];
  const currentProgressIndex = flowStep === 'profile'
    ? 3
    : progressSteps.indexOf(flowStep);

  const handleProfileCheckbox = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="progressive-onboarding">
      <div className="w-full max-w-2xl bg-cream rounded-2xl shadow-2xl max-h-[92vh] overflow-hidden flex flex-col border border-burgundy/10">

        {/* Progress Bar */}
        <div className="flex items-center gap-1.5 px-6 py-3 bg-burgundy/5 border-b border-burgundy/10 shrink-0">
          {progressLabels.map((label, i) => (
            <div key={label} className="flex items-center flex-1">
              <div className="flex items-center gap-1.5 flex-1">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-all duration-300 ${
                  i < currentProgressIndex ? 'bg-dark-teal text-cream' :
                  i === currentProgressIndex ? 'bg-burgundy text-cream ring-2 ring-burgundy/20 ring-offset-1' :
                  'bg-burgundy/10 text-burgundy/40'
                }`}>
                  {i < currentProgressIndex ? <CheckCircle className="w-4 h-4" /> : i + 1}
                </div>
                <span className={`text-xs font-medium hidden sm:block ${
                  i <= currentProgressIndex ? 'text-burgundy' : 'text-burgundy/40'
                }`}>{label}</span>
              </div>
              {i < 3 && <div className={`h-0.5 flex-1 mx-1.5 rounded transition-all duration-300 ${
                i < currentProgressIndex ? 'bg-dark-teal' : 'bg-burgundy/10'
              }`} />}
            </div>
          ))}
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1 p-6">

          {/* WELCOME */}
          {flowStep === 'welcome' && (
            <div className="text-center space-y-6" data-testid="onboarding-welcome">
              <div className="w-16 h-16 bg-gradient-to-br from-burgundy to-dark-teal rounded-2xl flex items-center justify-center mx-auto">
                <Sparkles className="w-8 h-8 text-cream" />
              </div>
              <div>
                <h2 className="text-2xl sm:text-3xl font-serif font-bold text-burgundy">
                  Welcome to Luminelle{user?.name ? `, ${user.name.split(' ')[0]}` : ''}!
                </h2>
                <p className="text-burgundy/70 mt-3 max-w-md mx-auto leading-relaxed">
                  Let's personalize your experience. In the next 2 minutes, we'll identify your primary wellness focus and connect you to the content that matters most.
                </p>
              </div>

              <div className="bg-white rounded-xl p-5 border border-burgundy/10 text-left max-w-md mx-auto">
                <h3 className="font-semibold text-burgundy text-sm mb-3">Here's what's ahead:</h3>
                <div className="space-y-3">
                  {[
                    { num: '1', text: '5 quick questions to find your focus area' },
                    { num: '2', text: 'Select specific symptoms you\'re experiencing' },
                    { num: '3', text: 'Get a personalized wellness plan with content matched to you' }
                  ].map(item => (
                    <div key={item.num} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-dark-teal/10 flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-dark-teal">{item.num}</span>
                      </div>
                      <p className="text-sm text-burgundy/80">{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col items-center gap-3">
                <Button
                  onClick={() => setFlowStep('part1')}
                  className="bg-gradient-to-r from-burgundy to-dark-teal text-cream hover:opacity-90 px-8 py-5 rounded-full text-base shadow-lg"
                  data-testid="onboarding-start-btn"
                >
                  Let's Get Started <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <button
                  onClick={handleSkip}
                  className="text-sm text-burgundy/50 hover:text-burgundy underline"
                  data-testid="onboarding-skip-btn"
                >
                  Skip for now
                </button>
              </div>
            </div>
          )}

          {/* PART 1: FOCUS AREA */}
          {flowStep === 'part1' && (
            <div data-testid="onboarding-part1">
              <div className="text-center mb-6">
                <h2 className="text-xl sm:text-2xl font-serif font-bold text-burgundy">Identify Your Focus</h2>
                <p className="text-burgundy/60 text-sm mt-1">5 questions to discover which area needs the most attention.</p>
              </div>
              <div className="space-y-4">
                {PART1_QUESTIONS.map((q, index) => (
                  <div key={q.id} className="bg-white rounded-xl p-4 border border-burgundy/10 shadow-sm" data-testid={`onboarding-q${index + 1}`}>
                    <h3 className="text-sm font-semibold text-burgundy mb-3">
                      <span className="text-dark-teal mr-2">{index + 1}.</span>{q.question}
                    </h3>
                    <div className="space-y-2">
                      {Object.entries(q.options).map(([letter, text]) => (
                        <label
                          key={letter}
                          className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all text-sm ${
                            part1Answers[q.id] === letter
                              ? 'border-burgundy bg-burgundy/5 shadow-sm'
                              : 'border-burgundy/15 hover:border-burgundy/30'
                          }`}
                          data-testid={`onboarding-q${index + 1}-option-${letter}`}
                        >
                          <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                            part1Answers[q.id] === letter ? 'border-burgundy bg-burgundy' : 'border-burgundy/30'
                          }`}>
                            {part1Answers[q.id] === letter && <div className="w-1.5 h-1.5 rounded-full bg-cream" />}
                          </div>
                          <input
                            type="radio"
                            name={q.id}
                            value={letter}
                            checked={part1Answers[q.id] === letter}
                            onChange={() => setPart1Answers(prev => ({ ...prev, [q.id]: letter }))}
                            className="sr-only"
                          />
                          <span className="text-burgundy/80">{text}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center pt-6">
                <Button
                  onClick={() => setFlowStep('welcome')}
                  variant="outline"
                  className="border-burgundy/20 text-burgundy hover:bg-burgundy/5"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                <Button
                  onClick={handlePart1Complete}
                  disabled={!isPart1Complete}
                  className="bg-gradient-to-r from-burgundy to-dark-teal text-cream hover:opacity-90 px-6 py-4 rounded-full disabled:opacity-40"
                  data-testid="onboarding-part1-continue"
                >
                  Continue <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* PART 2: SYMPTOMS */}
          {flowStep === 'part2' && (
            <div data-testid="onboarding-part2">
              <div className="text-center mb-4">
                <h2 className="text-xl sm:text-2xl font-serif font-bold text-burgundy">Select Your Symptoms</h2>
                <p className="text-burgundy/60 text-sm mt-1">
                  Check everything that applies. Your primary focus is expanded.
                </p>
              </div>
              {pillarOrder.map((pillarKey) => {
                const pillar = SYMPTOMS[pillarKey];
                const isPrimary = pillarKey === primaryPillar;
                const isExpanded = expandedPillar === pillarKey;
                const count = Object.keys(pillar.categories).reduce((s, c) => s + (part2Symptoms[c]?.length || 0), 0);
                return (
                  <div key={pillarKey} className={`mb-3 rounded-xl border-2 overflow-hidden transition-all ${isPrimary ? 'border-burgundy/30' : 'border-burgundy/10'}`}>
                    <button
                      onClick={() => setExpandedPillar(isExpanded ? null : pillarKey)}
                      className={`w-full flex items-center justify-between p-3 transition-colors ${isPrimary ? 'bg-burgundy/5' : 'bg-white hover:bg-cream/50'}`}
                      data-testid={`onboarding-pillar-${pillarKey.toLowerCase()}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          pillarKey === 'BODY' ? 'bg-burgundy text-cream' :
                          pillarKey === 'MIND' ? 'bg-dark-teal text-cream' :
                          'bg-gold text-burgundy'
                        }`}>{pillar.label}</span>
                        {isPrimary && <span className="text-[10px] font-medium text-burgundy bg-gold/30 px-1.5 py-0.5 rounded-full">Your Primary Focus</span>}
                        {count > 0 && <span className="text-xs text-burgundy/60">{count} selected</span>}
                      </div>
                      {isExpanded ? <ChevronUp className="h-4 w-4 text-burgundy/40" /> : <ChevronDown className="h-4 w-4 text-burgundy/40" />}
                    </button>
                    {isExpanded && (
                      <div className="p-3 border-t border-burgundy/10 bg-white">
                        {Object.entries(pillar.categories).map(([category, symptoms]) => (
                          <div key={category} className="bg-cream/50 rounded-lg p-3 border border-burgundy/10 mb-2 last:mb-0">
                            <h3 className="font-serif font-bold text-burgundy text-sm mb-2">{category}</h3>
                            <div className="grid grid-cols-1 gap-1.5">
                              {symptoms.map(symptom => (
                                <label key={symptom} className="flex items-center gap-2 cursor-pointer text-sm">
                                  <input
                                    type="checkbox"
                                    checked={part2Symptoms[category]?.includes(symptom) || false}
                                    onChange={() => {
                                      setPart2Symptoms(prev => {
                                        const current = prev[category] || [];
                                        return {
                                          ...prev,
                                          [category]: current.includes(symptom)
                                            ? current.filter(s => s !== symptom)
                                            : [...current, symptom]
                                        };
                                      });
                                    }}
                                    className="h-3.5 w-3.5 text-burgundy border-burgundy/30 rounded focus:ring-gold"
                                  />
                                  <span className="text-burgundy/80 text-xs">{symptom}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              <div className="flex justify-between items-center pt-4">
                <Button
                  onClick={() => setFlowStep('part1')}
                  variant="outline"
                  className="border-burgundy/20 text-burgundy hover:bg-burgundy/5"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={!hasPart2Symptoms || saving}
                  className="bg-gradient-to-r from-burgundy to-dark-teal text-cream hover:opacity-90 px-6 py-4 rounded-full disabled:opacity-40"
                  data-testid="onboarding-see-results"
                >
                  {saving ? 'Saving...' : 'See My Plan'} <CheckCircle className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* RESULTS */}
          {flowStep === 'results' && results && (
            <div className="space-y-5" data-testid="onboarding-results">
              {/* Primary Pillar Card */}
              <div className={`bg-gradient-to-br ${PILLAR_DETAILS[results.primary_pillar].color} rounded-2xl p-6 text-cream shadow-lg`}>
                <p className="text-cream/70 text-xs uppercase tracking-wider mb-1">Your Primary Focus</p>
                <h3 className="text-2xl sm:text-3xl font-serif font-bold text-white" style={{ textShadow: '0 0 20px rgba(212,175,55,0.6), 0 2px 4px rgba(0,0,0,0.3)' }}>
                  {PILLAR_DETAILS[results.primary_pillar].fullLabel}
                </h3>
                <p className="text-cream/85 text-sm leading-relaxed mt-2">
                  {PILLAR_DETAILS[results.primary_pillar].description}
                </p>
                {results.pillar_symptom_counts && (
                  <div className="mt-4 space-y-1.5">
                    {Object.entries(results.pillar_symptom_counts).filter(([_, c]) => c > 0).sort((a, b) => b[1] - a[1]).map(([p, c]) => (
                      <div key={p} className="flex items-center gap-3">
                        <span className="text-cream/80 text-xs w-14">{PILLAR_DETAILS[p].label}</span>
                        <div className="flex-1 h-1.5 bg-cream/20 rounded-full">
                          <div className="h-full bg-cream/70 rounded-full" style={{ width: `${Math.min(100, (c / results.total_symptoms) * 100)}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Priority Areas with Article Previews */}
              {results.top_categories?.length > 0 && (
                <div className="bg-white rounded-xl p-5 border border-burgundy/10 shadow-sm">
                  <h3 className="font-serif font-bold text-burgundy text-base mb-3">Your Priority Areas</h3>
                  <p className="text-burgundy/60 text-xs mb-4">Content in the Classroom matched to your symptoms:</p>
                  <div className="space-y-3">
                    {results.top_categories.map(cat => {
                      const articles = matchedArticles[cat] || [];
                      return (
                        <div key={cat} className="border border-burgundy/10 rounded-lg overflow-hidden">
                          <a
                            href={getCategoryClassroomUrl(cat)}
                            onClick={(e) => { e.preventDefault(); onComplete('classroom'); window.location.href = getCategoryClassroomUrl(cat); }}
                            className="flex items-center justify-between px-4 py-2.5 bg-burgundy text-cream hover:bg-burgundy/90 transition-colors"
                            data-testid={`result-tag-${cat.toLowerCase().replace(/[\s/]+/g, '-')}`}
                          >
                            <span className="text-sm font-semibold">{cat}</span>
                            <ArrowRight className="h-4 w-4" />
                          </a>
                          {articles.length > 0 && (
                            <div className="divide-y divide-burgundy/5">
                              {articles.map((article, i) => (
                                <a
                                  key={article.id || i}
                                  href={getCategoryClassroomUrl(cat)}
                                  onClick={(e) => { e.preventDefault(); onComplete('classroom'); window.location.href = getCategoryClassroomUrl(cat); }}
                                  className="flex items-center gap-2 px-4 py-2 hover:bg-cream/50 transition-colors"
                                >
                                  <BookOpen className="h-3 w-3 text-burgundy/40 shrink-0" />
                                  <span className="text-xs text-burgundy/70 truncate">{article.title}</span>
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* CTAs */}
              <div className="bg-gradient-to-r from-burgundy/5 to-dark-teal/5 rounded-2xl p-6 border border-burgundy/10">
                <h3 className="text-lg font-serif font-bold text-burgundy text-center mb-2">What would you like to do next?</h3>
                <p className="text-burgundy/60 text-sm text-center mb-5">You can always come back to these from your dashboard.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Button
                    onClick={() => { setFlowStep('profile'); setProfileStep(1); }}
                    className="bg-burgundy hover:bg-burgundy/90 text-cream py-5 rounded-xl text-sm font-medium"
                    data-testid="onboarding-setup-profile"
                  >
                    <User className="mr-2 h-4 w-4" /> Set Up My Profile
                  </Button>
                  <Button
                    onClick={handleExploreContent}
                    className="bg-dark-teal hover:bg-dark-teal/90 text-cream py-5 rounded-xl text-sm font-medium"
                    data-testid="onboarding-explore-content"
                  >
                    <BookOpen className="mr-2 h-4 w-4" /> Explore My Content
                  </Button>
                </div>
                <button
                  onClick={() => {
                    handleSkip();
                  }}
                  className="text-sm text-burgundy/50 hover:text-burgundy underline block mx-auto mt-4"
                  data-testid="onboarding-skip-to-dashboard"
                >
                  Go to Dashboard
                </button>
              </div>
            </div>
          )}

          {/* PROFILE SETUP (optional, triggered from results CTA) */}
          {flowStep === 'profile' && (
            <div data-testid="onboarding-profile">
              <div className="text-center mb-5">
                <h2 className="text-xl sm:text-2xl font-serif font-bold text-burgundy">Complete Your Profile</h2>
                <p className="text-burgundy/60 text-sm mt-1">Step {profileStep} of 4 — all optional</p>
              </div>

              {profileStep === 1 && (
                <div className="space-y-3">
                  <h3 className="text-base font-medium text-burgundy">Tell us a bit about yourself</h3>
                  <p className="text-burgundy/70 text-sm">Help the community get to know you!</p>
                  <textarea
                    value={profileData.fun_facts}
                    onChange={(e) => setProfileData({ ...profileData, fun_facts: e.target.value })}
                    placeholder="Share your hobbies, interests, or anything you'd like the community to know..."
                    className="w-full min-h-[100px] p-3 border border-burgundy/30 rounded-lg focus:border-gold focus:ring-1 focus:ring-gold resize-none text-sm"
                    data-testid="onboarding-fun-facts"
                  />
                </div>
              )}

              {profileStep === 2 && (
                <div className="space-y-3">
                  <h3 className="text-base font-medium text-burgundy">What are your wellness goals?</h3>
                  <p className="text-burgundy/70 text-sm">Select all that apply</p>
                  <div className="grid grid-cols-2 gap-2">
                    {goalsOptions.map(goal => (
                      <label key={goal} className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-all text-sm ${
                        profileData.wellness_goals.includes(goal) ? 'border-burgundy bg-burgundy/5' : 'border-burgundy/15 hover:border-burgundy/30'
                      }`}>
                        <input
                          type="checkbox"
                          checked={profileData.wellness_goals.includes(goal)}
                          onChange={() => handleProfileCheckbox('wellness_goals', goal)}
                          className="h-3.5 w-3.5 text-burgundy border-burgundy/30 rounded focus:ring-gold"
                        />
                        <span className="text-burgundy/80">{goal}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {profileStep === 3 && (
                <div className="space-y-3">
                  <h3 className="text-base font-medium text-burgundy">What topics interest you most?</h3>
                  <p className="text-burgundy/70 text-sm">Select all that apply</p>
                  <div className="grid grid-cols-2 gap-2">
                    {topicsOptions.map(topic => (
                      <label key={topic} className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-all text-sm ${
                        profileData.topics_of_interest.includes(topic) ? 'border-burgundy bg-burgundy/5' : 'border-burgundy/15 hover:border-burgundy/30'
                      }`}>
                        <input
                          type="checkbox"
                          checked={profileData.topics_of_interest.includes(topic)}
                          onChange={() => handleProfileCheckbox('topics_of_interest', topic)}
                          className="h-3.5 w-3.5 text-burgundy border-burgundy/30 rounded focus:ring-gold"
                        />
                        <span className="text-burgundy/80">{topic}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {profileStep === 4 && (
                <div className="space-y-3">
                  <h3 className="text-base font-medium text-burgundy">Where are you on your wellness journey?</h3>
                  <p className="text-burgundy/70 text-sm">This helps us tailor content to your needs</p>
                  <div className="space-y-2">
                    {experienceLevels.map(level => (
                      <label key={level.value} className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                        profileData.experience_level === level.value ? 'border-burgundy bg-burgundy/5' : 'border-burgundy/20 hover:bg-burgundy/5'
                      }`}>
                        <input
                          type="radio"
                          name="experience_level"
                          value={level.value}
                          checked={profileData.experience_level === level.value}
                          onChange={(e) => setProfileData({ ...profileData, experience_level: e.target.value })}
                          className="h-4 w-4 text-burgundy border-burgundy/30 focus:ring-gold"
                        />
                        <span className="text-sm text-burgundy">{level.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-between mt-6 pt-4 border-t border-burgundy/10">
                {profileStep > 1 ? (
                  <Button
                    variant="outline"
                    onClick={() => setProfileStep(profileStep - 1)}
                    className="border-burgundy/20 text-burgundy hover:bg-burgundy/5"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" /> Back
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => setFlowStep('results')}
                    className="border-burgundy/20 text-burgundy hover:bg-burgundy/5"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" /> Back to Results
                  </Button>
                )}
                {profileStep < 4 ? (
                  <Button
                    onClick={() => setProfileStep(profileStep + 1)}
                    className="bg-burgundy hover:bg-burgundy/80 text-cream"
                  >
                    Continue <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleProfileSubmit}
                    disabled={saving}
                    className="bg-gold hover:bg-gold/80 text-burgundy font-medium px-6"
                    data-testid="onboarding-complete-profile"
                  >
                    {saving ? 'Saving...' : 'Complete Setup'}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProgressiveOnboarding;
